import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Linking,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';

interface AboutModalProps {
  visible: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ visible, onClose }) => {
  const appVersion = '1.0.0';
  const buildNumber = '2025.12.29';

  const handleWebsitePress = () => {
    Linking.openURL('https://www.sgagymnastics.com');
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Icon name="close" size={28} color="#333333" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>About</Text>
          <View style={styles.headerSpacer} />
        </View>

        <ScrollView style={styles.content}>
          {/* App Logo & Name */}
          <View style={styles.logoSection}>
            <View style={styles.logoPlaceholder}>
              <Text style={styles.logoText}>SGA</Text>
            </View>
            <Text style={styles.appName}>SGA Mobile</Text>
            <Text style={styles.appTagline}>Employee Portal</Text>
          </View>

          {/* Version Info */}
          <View style={styles.infoSection}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Version</Text>
              <Text style={styles.infoValue}>{appVersion}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Build</Text>
              <Text style={styles.infoValue}>{buildNumber}</Text>
            </View>
          </View>

          {/* Company Info */}
          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>About SGA Gymnastics</Text>
            <Text style={styles.description}>
              SGA Gymnastics Management System provides comprehensive tools for
              gymnastics facilities to manage students, staff, schedules, and
              more. This mobile app enables employees to access essential
              features on the go.
            </Text>
          </View>

          {/* Features */}
          <View style={styles.infoSection}>
            <Text style={styles.sectionTitle}>Features</Text>
            <View style={styles.featureItem}>
              <Icon name="time-outline" size={20} color="#007AFF" />
              <Text style={styles.featureText}>Time Clock & Attendance</Text>
            </View>
            <View style={styles.featureItem}>
              <Icon name="calendar-outline" size={20} color="#007AFF" />
              <Text style={styles.featureText}>Schedule Management</Text>
            </View>
            <View style={styles.featureItem}>
              <Icon name="document-text-outline" size={20} color="#007AFF" />
              <Text style={styles.featureText}>Timesheet Access</Text>
            </View>
            <View style={styles.featureItem}>
              <Icon name="person-outline" size={20} color="#007AFF" />
              <Text style={styles.featureText}>Profile Management</Text>
            </View>
          </View>

          {/* Links */}
          <View style={styles.infoSection}>
            <TouchableOpacity style={styles.linkButton} onPress={handleWebsitePress}>
              <Icon name="globe-outline" size={20} color="#007AFF" />
              <Text style={styles.linkText}>Visit Website</Text>
              <Icon name="chevron-forward" size={20} color="#C7C7CC" />
            </TouchableOpacity>
          </View>

          {/* Copyright */}
          <View style={styles.copyrightSection}>
            <Text style={styles.copyrightText}>
              © 2025 SGA Gymnastics Management System
            </Text>
            <Text style={styles.copyrightText}>All rights reserved.</Text>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F8F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  closeButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333333',
  },
  headerSpacer: {
    width: 36,
  },
  content: {
    flex: 1,
  },
  logoSection: {
    alignItems: 'center',
    paddingVertical: 32,
    backgroundColor: '#FFFFFF',
    marginBottom: 16,
  },
  logoPlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 20,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  logoText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  appName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 4,
  },
  appTagline: {
    fontSize: 16,
    color: '#666666',
  },
  infoSection: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333333',
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  infoLabel: {
    fontSize: 16,
    color: '#666666',
  },
  infoValue: {
    fontSize: 16,
    color: '#333333',
    fontWeight: '500',
  },
  description: {
    fontSize: 15,
    color: '#666666',
    lineHeight: 22,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  featureText: {
    fontSize: 16,
    color: '#333333',
    marginLeft: 12,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  linkText: {
    flex: 1,
    fontSize: 16,
    color: '#007AFF',
    marginLeft: 12,
  },
  copyrightSection: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  copyrightText: {
    fontSize: 13,
    color: '#999999',
    marginBottom: 4,
  },
});

export default AboutModal;
